import React, { useCallback, useEffect, useRef, useState } from 'react'
import { pdfjs, Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { VideoLink } from 'lesson-builder/videoLinks'
import styles from 'css/components/lesson-builder/presentation-slide.module.css'

/* Renders ONE page of a lesson PDF at a declared size.
 *
 * See docs/BLUEPRINT-2026-10-13-presentation-slide-sizing.md. The three
 * sizing strategies below all existed already and are all still needed -- a
 * thumbnail rail, an editor column and a projector view genuinely want
 * different ones. What changed is that a caller now STATES which it wants.
 *
 * Previously the mode was selected by which props you left out: omitting
 * `width` meant "measure your own box". That made the caller depend on an
 * implementation detail rather than on an interface (Lego Law 1), gave the
 * signature no way to express which combinations are legal, and silently
 * ignored `width` when `containerWidth` was also passed. `sizing` is a
 * discriminant now, so an illegal combination does not compile.
 *
 * Why sizing cannot be left to CSS at all: react-pdf rasterises the page
 * canvas at whatever pixel width it is handed, so the width is a JavaScript
 * value. That is why several earlier measured passes could not move the
 * slide with stylesheet changes alone.
 */
const VideoLinksOverlay = ({ videoLinks }: { videoLinks: VideoLink[] }) => {
	if (!videoLinks.length) return null
	return (
		<div className={styles.videoLinks}>
			{videoLinks.map((link, i) => (
				<a
					key={link.url + i}
					className={styles.videoLinkButton}
					href={link.url}
					target="_blank"
					rel="noopener noreferrer"
					title={link.title || 'Watch video'}
				>
					<span className={styles.videoLinkIcon} aria-hidden="true">▶</span>
					{link.title ? (link.title.length > 40 ? link.title.slice(0, 37) + '…' : link.title) : 'Watch video'}
				</a>
			))}
		</div>
	)
}

/* Only reached where the box cannot be measured at all -- jsdom reports
   clientWidth 0, and so does a real box before first layout -- so something
   renders rather than nothing. It is a FALLBACK, not a default: 540 used to
   be the default width, which is why the lesson builder's slide rendered 540
   wide inside a card with room for 658. Do not let it become one again. */
const UNMEASURABLE_WIDTH = 540

/* Present mode rasterises once at this width and CSS-scales the result, so
   the projected slide stays sharp when the container is larger than the
   page's natural size. */
const RENDER_WIDTH = 1200

interface CommonProps {
	lessonPdf: string | null
	pageNumber: number
	/** Display-only: adds the `current` class. */
	isCurrent?: boolean
	className?: string
	videoLinks?: VideoLink[]
}

/* The discriminant. Each arm carries exactly the geometry its own strategy
   needs and nothing else, so there is no combination to get wrong. */
type SizingProps =
	/** Fills whatever box it is placed in; measures its OWN root to do it, so
	    no container geometry crosses the boundary. The lesson builder's editor
	    column. */
	| { sizing: 'fill' }
	/** A pinned pixel width, regardless of the box. The slide thumbnail rail. */
	| { sizing: 'fixed'; width: number }
	/** Rasterise at RENDER_WIDTH, then CSS-scale to fit the given container.
	    The delivery/projector view. */
	| { sizing: 'present'; containerWidth: number; containerHeight: number }

export type PresentationSlideProps = CommonProps & SizingProps

export const PresentationSlide = (props: PresentationSlideProps) => {
	const { lessonPdf, pageNumber, isCurrent = false, className = '', videoLinks = [] } = props
	pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

	/* `sidebar.js` is still JavaScript, so the union above cannot protect that
	   one call site. Fall back to 'fill' rather than throwing -- a wrong size
	   is recoverable, a blank slide in a lesson is not -- and say so loudly in
	   development. */
	const declared = (props as { sizing?: string }).sizing
	const sizing: SizingProps['sizing'] =
		declared === 'fixed' || declared === 'present' || declared === 'fill' ? declared : 'fill'
	useEffect(() => {
		if (process.env.NODE_ENV !== 'production' && declared !== sizing) {
			console.error(
				`PresentationSlide: sizing="${String(declared)}" is not one of fill | fixed | present; `
				+ 'falling back to "fill". An untyped (.js) caller is passing the wrong props.'
			)
		}
	}, [declared, sizing])

	const rootRef = useRef<HTMLDivElement>(null)
	const [measuredWidth, setMeasuredWidth] = useState<number | null>(null)
	const [scale, setScale] = useState(1)

	useEffect(() => {
		if (sizing !== 'fill') return
		const el = rootRef.current
		if (!el) return
		const measure = () => {
			const w = el.clientWidth
			if (w > 0) setMeasuredWidth(w)
		}
		measure()
		/* A ResizeObserver and not a window-resize listener: in the lesson
		   builder, collapsing the rail resizes this box without resizing the
		   window, and a measurement that silently never fires is worse than one
		   that falls back. */
		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', measure)
			return () => window.removeEventListener('resize', measure)
		}
		const observer = new ResizeObserver(measure)
		observer.observe(el)
		return () => observer.disconnect()
	}, [sizing])

	const container = sizing === 'present'
		? { width: (props as { containerWidth: number }).containerWidth,
			height: (props as { containerHeight: number }).containerHeight }
		: null

	const onPageRenderSuccess = useCallback(() => {
		if (!container) return
		/* Scoped to this component's own root. This used to be
		   `document.querySelector('.presentMode canvas')` -- a document-wide
		   search from inside the component, which happened to work only because
		   exactly one present-mode slide is ever mounted. A second one (a
		   preview, a split view, a transition that briefly mounts two) would
		   have scaled this slide from a DIFFERENT slide's canvas. */
		const canvas = rootRef.current?.querySelector('canvas')
		if (!canvas) return
		const ratio = window.devicePixelRatio || 1
		const canvasWidth = canvas.width / ratio
		const canvasHeight = canvas.height / ratio
		if (!canvasWidth || !canvasHeight) return
		setScale(Math.min(container.width / canvasWidth, container.height / canvasHeight) * 0.95)
	}, [container])

	const classes = (extra: string) =>
		`${extra} presentation-slide ${isCurrent ? 'current' : ''} ${className}`

	if (container) {
		return (
			<div
				ref={rootRef}
				className={classes(styles.presentMode)}
				style={{ width: container.width, height: container.height }}
			>
				<div style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
					<Document file={lessonPdf} externalLinkTarget="_blank" externalLinkRel="noopener noreferrer">
						<Page
							pageNumber={pageNumber}
							width={RENDER_WIDTH}
							canvasBackground="transparent"
							renderTextLayer={false}
							onRenderSuccess={onPageRenderSuccess}
						/>
					</Document>
				</div>
				<VideoLinksOverlay videoLinks={videoLinks} />
			</div>
		)
	}

	const fillsBox = sizing === 'fill'
	const pageWidth = fillsBox
		? (measuredWidth ?? UNMEASURABLE_WIDTH)
		: (props as { width: number }).width

	return (
		<div
			ref={rootRef}
			className={classes(`${styles.presentationSlide} ${fillsBox ? styles.fillsBox : ''}`)}
		>
			<Document file={lessonPdf} externalLinkTarget="_blank" externalLinkRel="noopener noreferrer">
				<Page pageNumber={pageNumber} width={pageWidth} canvasBackground="transparent" />
			</Document>
			<VideoLinksOverlay videoLinks={videoLinks} />
		</div>
	)
}
